/**
 * Customer-logo upload (M4 — customer-logo pipeline, public bundle).
 *
 * # Why this module exists
 *
 * The checkout lets a customer attach their brand logo. Until M4 the real
 * `File` bytes lived only in component state and were NEVER uploaded — only
 * the filename + size were forwarded in the order. M4 uploads the bytes FIRST
 * to the admin's anonymous `POST /api/send/logo`, then threads the returned
 * `{ container, path, contentType, sizeBytes }` pointer into the subsequent
 * `/api/send` order POST as `design.logoBlob`.
 *
 * # The contract (mirrors `apps/admin/app/api/send/logo/route.ts`)
 *
 *   - multipart/form-data, one `file` part per logo (F15 — a company can have
 *     several). ONE request carries the whole set.
 *   - Success: 2xx (the server returns 201) with `logos: [pointer, …]`, plus
 *     the first pointer mirrored at the top level for older clients.
 *   - 413 a logo over the 2MB cap or the set over the 6MB total · 415 wrong
 *     type (PNG/JPEG/WebP only; SVG rejected) · 429 rate-limited · 503 storage
 *     unavailable · 403 origin not allowed · 400 bad request or too many files.
 *
 * # URL resolution
 *
 * The upload targets the SAME admin base `/api/send` resolves against
 * (`VITE_ADMIN_BASE_URL`), via `resolveAdminApiUrl` — one env-read, one
 * trailing-slash strip, so a mis-route regresses in one place.
 *
 * # Error discipline (SECURITY_RULES)
 *
 * Non-2xx and network failures are mapped to a typed `LogoUploadError` with a
 * stable, user-meaningful message. The raw server body is NEVER surfaced to
 * the customer (it could leak internal detail). Because the logo is OPTIONAL,
 * the caller catches this and lets the order proceed without the logo.
 */

import { checkLogoSelection, MAX_LOGO_COUNT } from '@brandbox/content-schema';
import { resolveAdminApiUrl } from './sendApi';

/** The blob pointer the upload returns — exactly `design.logoBlob`'s shape. */
export interface LogoBlobPointer {
  container: string;
  path: string;
  contentType: string;
  sizeBytes: number;
}

/** Classified, user-meaningful reasons an upload can fail. */
export type LogoUploadFailureReason =
  | 'too-large' // 413 — over the per-logo or total byte cap
  | 'wrong-type' // 415 — not PNG/JPEG/WebP (e.g. SVG)
  | 'rate-limited' // 429 — too many uploads from this IP
  | 'unavailable' // 503 — storage not configured / down
  | 'forbidden' // 403 — origin not allowed
  | 'invalid' // 400 — malformed request / empty file
  | 'too-many' // F15 — more logos than an order may carry (refused locally)
  | 'network' // fetch rejected (offline / DNS / CORS preflight)
  | 'unknown'; // any other non-2xx, or a malformed 2xx body

/**
 * A typed upload failure. `reason` lets the UI branch (retry vs. choose a
 * different file); `message` is safe to show the customer verbatim.
 */
export class LogoUploadError extends Error {
  readonly reason: LogoUploadFailureReason;
  constructor(reason: LogoUploadFailureReason, message: string) {
    super(message);
    this.name = 'LogoUploadError';
    this.reason = reason;
  }
}

/** Resolve the `POST /api/send/logo` URL against the shared admin base. */
export function resolveLogoUploadUrl(): string {
  return resolveAdminApiUrl('/api/send/logo');
}

/** Friendly, leak-free copy per failure reason. */
function messageFor(reason: LogoUploadFailureReason): string {
  switch (reason) {
    case 'too-many':
      // Only reachable from the local pre-check, which supplies its own
      // message naming the limit. This is the belt-and-braces fallback.
      return `You can attach up to ${MAX_LOGO_COUNT} logos.`;
    case 'too-large':
      return 'That logo is too large (max 2 MB). Pick a smaller file, or submit without it and email it later.';
    case 'wrong-type':
      return 'That file type is not supported (use a PNG, JPEG or WebP). You can submit without it and email it later.';
    case 'rate-limited':
      return "Too many uploads just now — wait a moment and retry, or submit without the logo and email it later.";
    case 'unavailable':
    case 'network':
    case 'unknown':
    default:
      return "We couldn't attach your logo right now. You can retry, or submit without it and email it later.";
    case 'forbidden':
      return "We couldn't attach your logo right now. You can submit without it and email it later.";
    case 'invalid':
      return "That logo couldn't be processed. Try another file, or submit without it and email it later.";
  }
}

/** Map an HTTP status onto a classified reason. */
function reasonForStatus(status: number): LogoUploadFailureReason {
  switch (status) {
    case 413:
      return 'too-large';
    case 415:
      return 'wrong-type';
    case 429:
      return 'rate-limited';
    case 503:
      return 'unavailable';
    case 403:
      return 'forbidden';
    case 400:
      return 'invalid';
    default:
      return 'unknown';
  }
}

function isPointer(value: unknown): value is LogoBlobPointer {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.container === 'string' &&
    v.container.length > 0 &&
    typeof v.path === 'string' &&
    v.path.length > 0 &&
    typeof v.contentType === 'string' &&
    v.contentType.length > 0 &&
    typeof v.sizeBytes === 'number'
  );
}

/**
 * Read the pointers out of a 2xx body.
 *
 * Two accepted shapes, and the difference is a rolling deploy, not a nicety:
 *
 *   - `{ container, path, ..., logos: [...] }` — F15 and later. `logos` wins.
 *   - `{ container, path, ... }` — an admin deployed before F15. One pointer.
 *
 * Returns null when the body is neither.
 */
function pointersFrom(body: unknown): LogoBlobPointer[] | null {
  if (!body || typeof body !== 'object') return null;
  const list = (body as { logos?: unknown }).logos;
  if (Array.isArray(list)) {
    if (!list.every(isPointer)) return null;
    return list.map((p) => ({
      container: p.container,
      path: p.path,
      contentType: p.contentType,
      sizeBytes: p.sizeBytes,
    }));
  }
  if (!isPointer(body)) return null;
  return [
    {
      container: body.container,
      path: body.path,
      contentType: body.contentType,
      sizeBytes: body.sizeBytes,
    },
  ];
}

/**
 * Upload every logo's bytes in ONE request and resolve one stored-blob pointer
 * per file, in the order the files were given.
 *
 * # Why one request (F15)
 *
 * The endpoint's per-IP rate limit spends one token per REQUEST. Uploading five
 * logos as five requests would burn a customer's budget five times faster than
 * a single logo does — and a customer who retries after a rejected file could
 * 429 themselves out of their own order. One request per submission keeps the
 * multi-logo path exactly as expensive as the single-logo path.
 *
 * # All-or-nothing
 *
 * The endpoint validates the set as a whole and stores nothing if any file is
 * refused, so there is no "some uploaded, some didn't" state to represent. This
 * function mirrors that: it resolves with every pointer or throws. The caller
 * MUST treat logos as optional — catch the error, surface the (already
 * friendly) `message`, and still allow the order to submit with no blobs.
 *
 * A count mismatch between what we sent and what came back is an error rather
 * than a partial success: attaching one blob to three filenames would claim the
 * wrong artwork for two of them.
 *
 * Note: we deliberately do NOT set a `Content-Type` header — the browser sets
 * `multipart/form-data; boundary=…` itself when the body is a `FormData`.
 */
export async function uploadLogos(
  files: readonly File[],
  signal?: AbortSignal,
): Promise<LogoBlobPointer[]> {
  // No logo is the common case; posting an empty request would spend a
  // rate-limit token to earn a 400.
  if (files.length === 0) return [];

  // The same limits the endpoint enforces. Checking here too means the customer
  // hears about a broken selection immediately, with a message naming the file
  // or the limit, instead of after a round trip that costs them a token.
  const check = checkLogoSelection(files);
  if (!check.ok) {
    const reason: LogoUploadFailureReason =
      check.reason === 'too-many' ? 'too-many' : 'too-large';
    throw new LogoUploadError(reason, check.message);
  }

  const form = new FormData();
  for (const file of files) form.append('file', file, file.name);

  let response: Response;
  try {
    const init: RequestInit = { method: 'POST', body: form };
    if (signal) init.signal = signal;
    response = await fetch(resolveLogoUploadUrl(), init);
  } catch {
    // Network-layer failure (offline, DNS, blocked CORS preflight). Never
    // surface the raw error text.
    throw new LogoUploadError('network', messageFor('network'));
  }

  if (!response.ok) {
    // Drain the body so the connection can be reused, but DISCARD it — the
    // customer never sees raw server text.
    await response.text().catch(() => undefined);
    const reason = reasonForStatus(response.status);
    throw new LogoUploadError(reason, messageFor(reason));
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new LogoUploadError('unknown', messageFor('unknown'));
  }
  const pointers = pointersFrom(body);
  if (!pointers || pointers.length !== files.length) {
    throw new LogoUploadError('unknown', messageFor('unknown'));
  }
  return pointers;
}

/**
 * Upload ONE logo — the single-file case of {@link uploadLogos}, kept because
 * one logo is still the common order and reads better at the call site.
 */
export async function uploadLogo(
  file: File,
  signal?: AbortSignal,
): Promise<LogoBlobPointer> {
  const [pointer] = await uploadLogos([file], signal);
  if (!pointer) throw new LogoUploadError('unknown', messageFor('unknown'));
  return pointer;
}
